import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { AddCircle, RemoveCircle } from "@mui/icons-material";
import apiService from "../../../../../ApiService";
import { API_ENDPOINTS } from "../../../../../ApiService";
import { MainContainer } from "../../../styles/sharedStyles";
import Title from "../../../../components/title/Title";
import Section from "../../../../components/section/Section";
import { tokens } from "../../../../../theme";
import ExerciseListing from "./ExerciseListing";
import InfoTextFieldWithDialog from "../../../../components/tooltip/infoTextFieldWithDialog/InfoTextFieldWithDialog";
import InfoSelectWithDialog from "../../../../components/tooltip/infoSelectWithDialog/InfoSelectWithDialog";

// Extracted style for the heading
const headingStyle = {
  backgroundColor: "#1976d2",
  color: "#fff",
  p: 1,
  borderRadius: 1,
};

const ExerciseCreationPage = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Form state
  const [name, setName] = useState("");
  const [frenchName, setFrenchName] = useState("");
  const [nameError, setNameError] = useState("");
  const [mainFamily, setMainFamily] = useState(null);
  const [bodyPart, setBodyPart] = useState(null);
  const [description, setDescription] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [execution, setExecution] = useState("");
  const [notes, setNotes] = useState("");
  const [steps, setSteps] = useState([{ stepOrder: 1, description: "" }]);
  const [equipmentNames, setEquipmentNames] = useState([]);
  const [tagNames, setTagNames] = useState([]);
  const [newTagInput, setNewTagInput] = useState("");
  const [intensities, setIntensities] = useState([
    { muscleGroup: null, intensity: "" },
  ]);

  // Data lists for dropdowns/checkboxes
  const [availableEquipments, setAvailableEquipments] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [availableMuscles, setAvailableMuscles] = useState([]);
  const [allExercises, setAllExercises] = useState([]);
  const [availableFamilies, setAvailableFamilies] = useState([]);
  const [availableBodyPart, setAvailableBodyPart] = useState([]);
  const [filter, setFilter] = useState("");

  // Use aggregated endpoint to fetch all creation data in one call.
  useEffect(() => {
    const fetchCreationData = async () => {
      try {
        const response = await apiService.get(
          API_ENDPOINTS.FITNESS_GET_INFO_FOR_EXERCISE_CREATION
        );
        console.log("Equipments:", response.data.equipments);
        const data = response.data;
        setAllExercises(data.exercises);
        setAvailableEquipments(data.equipments);
        setAvailableTags(data.tags);
        setAvailableMuscles(data.muscles);
        setAvailableFamilies(data.families);
        setAvailableBodyPart(data.bodyParts);
      } catch (error) {
        console.error("Error fetching creation data:", error);
      }
    };
    fetchCreationData();
  }, []);

  // Validate exercise name uniqueness as user types
  useEffect(() => {
    if (name) {
      const duplicate = allExercises.find(
        (ex) => ex.name.toLowerCase() === name.toLowerCase()
      );
      setNameError(
        duplicate ? "An exercise with this name already exists." : ""
      );
    } else {
      setNameError("");
    }
  }, [name, allExercises]);

  // Handlers for dynamic steps
  const handleStepChange = (index, value) => {
    const newSteps = [...steps];
    newSteps[index].description = value;
    setSteps(newSteps);
  };

  const addStep = () => {
    setSteps([...steps, { stepOrder: steps.length + 1, description: "" }]);
  };

  const removeStep = (index) => {
    const newSteps = steps.filter((_, i) => i !== index);
    // Optionally, re-number steps here
    setSteps(newSteps);
  };

  // Handlers for dynamic intensities
  const handleIntensityChange = (index, field, value) => {
    const newIntensities = [...intensities];
    newIntensities[index][field] = value;
    setIntensities(newIntensities);
  };

  const addIntensity = () => {
    setIntensities([...intensities, { muscleGroup: null, intensity: "" }]);
  };

  const removeIntensity = (index) => {
    const newIntensities = intensities.filter((_, i) => i !== index);
    setIntensities(newIntensities);
  };

  // Handlers for equipments and tags checkboxes
  const handleEquipmentChange = (event) => {
    const equipmentName = event.target.value;
    if (event.target.checked) {
      setEquipmentNames([...equipmentNames, equipmentName]);
    } else {
      setEquipmentNames(
        equipmentNames.filter((name) => name !== equipmentName)
      );
    }
  };

  const handleTagChange = (event) => {
    const tagName = event.target.value;
    if (event.target.checked) {
      setTagNames([...tagNames, tagName]);
    } else {
      setTagNames(tagNames.filter((name) => name !== tagName));
    }
  };

  // Form submission handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name,
        frenchName,
        mainFamilyName: mainFamily?.name || "", // send only the name string
        bodyPartName: bodyPart?.name || "", // send only the name string
        description,
        videoUrl,
        execution,
        notes,
        steps,
        equipmentNames,
        tagNames,
        intensities: intensities.map((intensity) => ({
          muscleGroupId: intensity.muscleGroup
            ? intensity.muscleGroup.id
            : null,
          intensity: intensity.intensity,
        })),
      };
      await apiService.post(API_ENDPOINTS.FITNESS_ADD_EXERCISE, payload);
      // Clear form fields after creation
      // Clear form fields after creation
      setName("");
      setFrenchName("");
      setMainFamily("");
      setBodyPart("");
      setDescription("");
      setVideoUrl("");
      setExecution("");
      setNotes("");
      setSteps([{ stepOrder: 1, description: "" }]);
      setEquipmentNames([]);
      setTagNames([]);
      setIntensities([{ muscleGroup: null, intensity: "" }]);
    } catch (error) {
      console.error("Error creating exercise:", error);
    }
  };

  // Basic filtering for the listing table (you can integrate the dropdown filters from your other component)
  const filteredExercises = allExercises.filter((ex) =>
    ex.name.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <MainContainer>
      <Title
        title="Create Exercise"
        summary="Fill out the form below to add a new exercise"
      />
      <Section>
        <Paper
          elevation={3}
          sx={{
            p: 1.5, // smaller padding
            mb: 2,
            backgroundColor: colors.primary[400],
          }}
        >
          <Typography variant="body1" color={colors.grey[100]} align="left">
            Please enter the details for the new exercise.
          </Typography>
        </Paper>
      </Section>
      <Box
        sx={{
          my: isMobile ? 1 : 2,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          width: "100%",
        }}
      >
        <Grid container justifyContent="center">
          <Grid
            item
            xs={12}
            sm={10}
            md={8}
            lg={6}
            sx={{ p: isMobile ? 1 : 1.5 }}
          >
            <Box sx={{ p: 1 }}>
              {/* Form Section */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <InfoTextFieldWithDialog
                        label="Exercise Name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        infoTitle="Exercise Name"
                        error={!!nameError}
                        helperText={nameError}
                        infoDescription="Enter a unique exercise name."
                        autoComplete="off"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <InfoTextFieldWithDialog
                        label="French Exercise Name"
                        value={frenchName}
                        onChange={(e) => setFrenchName(e.target.value)}
                        infoTitle="French Exercise Name"
                        infoDescription="Enter a unique french exercise name."
                        autoComplete="off"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete
                        options={
                          availableFamilies
                            ? availableFamilies
                                .slice()
                                .sort((a, b) => a.name.localeCompare(b.name))
                            : []
                        }
                        getOptionLabel={(option) => option.name}
                        value={mainFamily}
                        onChange={(event, newValue) => setMainFamily(newValue)}
                        groupBy={(option) => option.name[0].toUpperCase()} // groups by the first letter
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Main Families"
                            variant="outlined"
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete
                        options={
                          availableBodyPart
                            ? availableBodyPart
                                .slice()
                                .sort((a, b) => a.name.localeCompare(b.name))
                            : []
                        }
                        getOptionLabel={(option) => option.name}
                        value={bodyPart}
                        onChange={(event, newValue) => setBodyPart(newValue)}
                        groupBy={(option) => option.name[0].toUpperCase()} // groups by the first letter
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Body Part"
                            variant="outlined"
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Description"
                        fullWidth
                        multiline
                        rows={3}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        variant="outlined"
                        autoComplete="off"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Video URL"
                        fullWidth
                        value={videoUrl}
                        onChange={(e) => setVideoUrl(e.target.value)}
                        variant="outlined"
                        autoComplete="off"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel>Execution</InputLabel>
                        <Select
                          value={execution}
                          label="Execution"
                          onChange={(e) => setExecution(e.target.value)}
                        >
                          <MenuItem value="UNILATERAL">Unilateral</MenuItem>
                          <MenuItem value="BILATERAL">Bilateral</MenuItem>
                          <MenuItem value="BOTH">Both</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Notes"
                        fullWidth
                        multiline
                        rows={2}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        variant="outlined"
                      />
                    </Grid>
                    {/* Steps Section */}
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        Steps
                      </Typography>
                      {steps.map((step, index) => (
                        <Grid
                          container
                          spacing={2}
                          key={index}
                          alignItems="center"
                          sx={{ mb: 1 }}
                        >
                          <Grid item xs={10}>
                            <TextField
                              label={`Step ${index + 1}`}
                              value={step.description}
                              onChange={(e) =>
                                handleStepChange(index, e.target.value)
                              }
                              fullWidth
                              multiline
                              variant="outlined"
                            />
                          </Grid>
                          <Grid item xs={2}>
                            <IconButton
                              color="error"
                              onClick={() => removeStep(index)}
                            >
                              <RemoveCircle />
                            </IconButton>
                          </Grid>
                        </Grid>
                      ))}
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={addStep}
                        startIcon={<AddCircle />}
                      >
                        Add Step
                      </Button>
                    </Grid>
                    {/* Intensities Section */}
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        Intensities
                      </Typography>
                      {intensities.map((intensity, index) => (
                        <Grid
                          container
                          spacing={2}
                          key={index}
                          alignItems="center"
                          sx={{ mb: 1 }}
                        >
                          <Grid item xs={6}>
                            <Autocomplete
                              options={availableMuscles}
                              getOptionLabel={(option) => option.name}
                              value={intensity.muscleGroup}
                              onChange={(event, newValue) =>
                                handleIntensityChange(
                                  index,
                                  "muscleGroup",
                                  newValue
                                )
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Muscle Group"
                                  variant="outlined"
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={4}>
                            <TextField
                              label="Intensity"
                              fullWidth
                              value={intensity.intensity}
                              onChange={(e) =>
                                handleIntensityChange(
                                  index,
                                  "intensity",
                                  e.target.value
                                )
                              }
                              variant="outlined"
                            />
                          </Grid>
                          <Grid item xs={2}>
                            <IconButton
                              color="error"
                              onClick={() => removeIntensity(index)}
                            >
                              <RemoveCircle />
                            </IconButton>
                          </Grid>
                        </Grid>
                      ))}
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={addIntensity}
                        startIcon={<AddCircle />}
                      >
                        Add Intensity
                      </Button>
                    </Grid>
                    {/* Equipments Section */}
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom sx={headingStyle}>
                        Select Equipments
                      </Typography>
                      {Object.entries(
                        availableEquipments.reduce((acc, equip) => {
                          const type = equip.equipmentType || "Other";
                          if (!acc[type]) {
                            acc[type] = [];
                          }
                          acc[type].push(equip);
                          return acc;
                        }, {})
                      )
                        .sort(([typeA], [typeB]) => typeA.localeCompare(typeB))
                        .map(([type, equipments]) => (
                          <Box key={type} sx={{ mb: 2 }}>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: "bold",
                                color: theme.palette.primary.main,
                                textTransform: "capitalize", // ensures first letter is uppercase
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                pb: 1,
                                mb: 1,
                              }}
                            >
                              {type.toLowerCase().replace(/_/g, " ")}
                            </Typography>
                            <FormGroup row>
                              {equipments.map((equip) => (
                                <FormControlLabel
                                  key={equip.id}
                                  control={
                                    <Checkbox
                                      value={equip.name}
                                      checked={equipmentNames.includes(
                                        equip.name
                                      )}
                                      onChange={handleEquipmentChange}
                                    />
                                  }
                                  label={equip.name}
                                />
                              ))}
                            </FormGroup>
                          </Box>
                        ))}
                    </Grid>
                    {/* Tags Section */}
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom sx={headingStyle}>
                        Select Tags
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        <FormGroup row>
                          {availableTags.map((tag) => (
                            <FormControlLabel
                              key={tag.id}
                              control={
                                <Checkbox
                                  value={tag.name}
                                  checked={tagNames.includes(tag.name)}
                                  onChange={handleTagChange}
                                />
                              }
                              label={tag.name}
                            />
                          ))}
                        </FormGroup>
                        <TextField
                          label="Add New Tag"
                          variant="outlined"
                          value={newTagInput}
                          onChange={(e) => setNewTagInput(e.target.value)}
                          onBlur={() => {
                            if (
                              newTagInput &&
                              !tagNames.includes(newTagInput)
                            ) {
                              setTagNames([...tagNames, newTagInput]);
                              setNewTagInput("");
                            }
                          }}
                          placeholder="Type a new tag and click away"
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth
                      >
                        Create Exercise
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Reusable Exercises Table */}

      <ExerciseListing
        exercises={allExercises}
        headerColor={tokens(theme.palette.mode).primary[900]}
      />
    </MainContainer>
  );
};

export default ExerciseCreationPage;
